/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
/**
 * @fileoverview OpenPay logic module.
 * Handles payment processing operations including store charges
 * and card charges through the OpenPay payment gateway.
 * @module logic/openpay
 */

import { refreshToken, signOut } from "../../utils/common.js";
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";

/**
 * Creates a store charge for cash payments.
 * @async
 * @param {string} userId - User identifier
 * @param {Object} storeChargeData - Store charge configuration data
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} Store charge response data or null on error
 */
export const createStoreCharge = async (userId, storeChargeData, navigate) => {
  logger.info("Store charge data received to create:", {
    data: storeChargeData,
  });
  logger.info("User ID received to create store charge:", { data: userId });

  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(storeChargeData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}openpay/store/${userId}`,
      options,
    );

    const result = await response.json();

    logger.info("Create store charge response data: ", { data: result.data });

    if (result.data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await createStoreCharge(userId, storeChargeData, navigate);
    }

    return result.data;
  } catch (error) {
    logger.error("Error creating store charge:", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Creates a card charge for credit/debit card payments.
 * @async
 * @param {string} userId - User identifier
 * @param {Object} cardChargeData - Card charge configuration data
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} Card charge response data or null on error
 */
export const createCardCharge = async (userId, cardChargeData, navigate) => {
  logger.info("Card charge data received to create:", { data: cardChargeData });
  logger.info("User ID received to create card charge:", { data: userId });

  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(cardChargeData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}openpay/card/${userId}`,
      options,
    );

    const result = await response.json();

    logger.info("Create card charge response data: ", { data: result });

    if (result.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await createCardCharge(userId, cardChargeData, navigate);
    }

    if (!response.ok) {
      // Handle API errors (400, 500, etc.)
      logger.error("Card charge API error:", { data: result });
      return null;
    }

    return result.data;
  } catch (error) {
    logger.error("Error creating card charge:", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Gets the status of a card charge.
 * Used to verify payment status after 3DS authentication.
 * @async
 * @param {string} chargeId - Charge identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} Charge status data or null on error
 */
export const getCardChargeStatus = async (chargeId, navigate) => {
  logger.info("Getting card charge status for charge ID:", { data: chargeId });

  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}openpay/card/status/${chargeId}`,
      options,
    );

    const result = await response.json();

    logger.info("Get card charge status response data: ", { data: result });

    if (result.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getCardChargeStatus(chargeId, navigate);
    }

    if (!response.ok) {
      throw new Error(
        apiMessageOrGeneric(result.message, "FAILED_TO_VERIFY_PAYMENT"),
      );
    }

    return result.data;
  } catch (error) {
    logger.error("Error getting card charge status:", {
      err: sanitizeError(error),
    });
    return null;
  }
};
